import {
    Alert,
    AlertIcon,
    AlertTitle,
    Avatar,
    AvatarGroup,
    Box,
    Breadcrumb,
    BreadcrumbLink,
    BreadcrumbItem,
    Button,
    ButtonGroup,
    ButtonProps,
    Divider,
    Grid,
    GridItem,
    Heading,
    HStack,
    Icon,
    IconButton,
    Menu,
    MenuButton,
    MenuDivider,
    MenuItem,
    MenuList,
    SimpleGrid,
    StackDivider,
    Tag,
    Text,
    TextProps,
    Tooltip,
    VStack,
    Wrap,
    Popover,
    PopoverTrigger,
    PopoverContent,
    PopoverBody,
    useDisclosure,
    BoxProps,
    InputGroup,
    Input,
    InputLeftElement,
    Progress,
    List,
    ListItem,
    Spinner,
    Link as ChakraLink,
    Table,
    Tbody,
    Tr,
    Td,
    Thead,
    Th,
    Skeleton,
    Center,
    StackProps,
    InputRightElement,
    useTheme,
} from '@chakra-ui/react';
import { ChevronDownIcon, ChevronRightIcon } from '@chakra-ui/icons';
import React, { useRef } from 'react';
import { ReportingViewBaseConfig } from '../../../base';
import { DocumentEditorViewProps } from './documentEditorProps';
import {
    BlockItemViewProps,
    ButtonBlockViewProps,
    CalloutBlockViewProps,
    HeadingBlockViewProps,
    ListItemBlockViewProps,
    MentionPreviewViewProps,
    MentionSpanViewProps,
    ResourceBlockViewProps,
    TaskBlockViewProps,
    TextBlockViewProps,
    ValueFieldContainerProps,
} from '../../../view';
import { ToolbarItemContainerProps, ToolbarItemViewProps } from '../../../view/toolbar';
import { BiCommentDetail } from 'react-icons/bi';
import { AiOutlineClose, AiOutlineEdit, AiOutlineHistory } from 'react-icons/ai';
import { HiOutlineDotsVertical } from 'react-icons/hi';
import { createDatabaseBlockView } from './database';
import { BsArrowRightShort, BsDot, BsPlus } from 'react-icons/bs';
import { EditorTheme, CustomNodeProps } from '@varos/editor-core';
import { HiOutlineSlash } from 'react-icons/hi2';
import { PiMagicWand } from 'react-icons/pi';
import { AppCustomNode, AppEditorTheme } from '@varos/research-editor';
import { AppCustomPropsUnion } from '@varos/research-editor/lib/node/nodeProps';
import { assertNever } from '@varos/util-typescript';
import { FiCheck, FiX } from 'react-icons/fi';

// Import all the extracted components
import { createDocumentItemView } from './item/documentItemView';
import { createDocumentResourceView } from './resource/documentResourceView';
import { createDocumentButtonView } from './button/documentButtonView';
import { createDocumentMentionView } from './mention/documentMentionView';
import { createDocumentMentionPreviewView } from './mention-preview/documentMentionPreviewView';
import { createDocumentTaskView } from './task/documentTaskView';
import { createDocumentTextView } from './text/documentTextView';
import { createDocumentHeadingView } from './heading/documentHeadingView';
import { createDocumentListItemView } from './list-item/documentListItemView';
import { createDocumentCalloutView } from './callout/documentCalloutView';
import { createDocumentToolbarView } from './toolbar/documentToolbarView';
import { ShortcutListViewProps } from './shortcut/list';

import { ThemeTypings } from '@chakra-ui/react';
import { createDocumentFormView } from './form';

/**
 * Transparentize Chakra UI color tokens
 * @param color - Chakra UI (semantic) token
 * @param opacity - Opacity value 0 to 1.
 */
export const alpha = (color: ThemeTypings['colors'], value: number) => {
    const key = color.replaceAll('.', '-');
    return `color-mix(in srgb, var(--chakra-colors-${key}) ${Math.max(Math.min(value * 100, 100), 0)}%, transparent)`;
};

export function createDocumentEditorView(
    config: ReportingViewBaseConfig & {
        Container: ReportingViewBaseConfig['Container'] & {
            Value: {
                Field: React.FC<ValueFieldContainerProps>;
            };
        };
    }
): React.FC<DocumentEditorViewProps> {
    const {
        Container: {
            Block: {
                Button: ButtonBlockContainer,
                Resource: ResourceBlockContainer,
                DatabaseView: DatabaseViewBlockContainer,
                Task: TaskBlockContainer,
                Text: TextBlockContainer,
                Heading: HeadingBlockContainer,
                ListItem: ListItemBlockContainer,
                Callout: CalloutBlockContainer,
                Form: FormBlockContainer,
            },
            Shortcut: { List: ShortcutContainer },
            Span: { Mention: MentionSpanContainer },
            Toolbar: { Item: ToolbarItemContainer },
        },
        infra: {
            editor: { Component: Editor },
        },
    } = config;

    // Create all the components
    const BlockItem = createDocumentItemView(config);
    const ResourceBlock = createDocumentResourceView({ ...config, BlockItem });
    const BlockItemButton = createDocumentButtonView({ ...config, BlockItem });
    const MentionPreview = createDocumentMentionPreviewView(config);
    const Mention = createDocumentMentionView({ ...config, MentionPreview });
    const Task = createDocumentTaskView({ ...config, BlockItem });
    const TextBlock = createDocumentTextView({ ...config, BlockItem });
    const HeadingBlock = createDocumentHeadingView({ ...config, BlockItem });
    const ListItemBlock = createDocumentListItemView({ ...config, BlockItem });
    const CalloutBlock = createDocumentCalloutView({ ...config, BlockItem });
    const SelectionToolbar = createDocumentToolbarView(config);
    const FormBlock = createDocumentFormView({ ...config, BlockItem });
    const DatabaseBlock = createDatabaseBlockView({
        ...config,
        BlockItem: BlockItem,
    });

    const styles = {
        offset: 9,
    };

    const ShortcutView: React.FC<ShortcutListViewProps> = (props) => {
        return (
            <VStack align="start" spacing={2} w="full">
                <Text color="whiteAlpha.600" fontSize="sm" fontWeight="medium">
                    Get started
                </Text>
                <HStack spacing={2} wrap="wrap" justify="center" align="center">
                    <Tooltip
                        label="Browse and select from available templates"
                        placement="top"
                        openDelay={300}
                        px={2}
                        py={1}
                        bg="gray.50"
                        color="gray.900"
                        borderRadius="md"
                        closeOnClick={true}
                        shouldWrapChildren={true}
                    >
                        <Button
                            size="sm"
                            variant="outline"
                            borderColor="whiteAlpha.300"
                            color="whiteAlpha.800"
                            bg="none"
                            _hover={{
                                bg: 'whiteAlpha.100',
                                borderColor: 'whiteAlpha.400',
                            }}
                            leftIcon={<Text fontSize="md">📄</Text>}
                        >
                            Template
                        </Button>
                    </Tooltip>

                    <Text
                        color="whiteAlpha.500"
                        fontSize="xs"
                        fontWeight="semibold"
                        px={2}
                    >
                        OR
                    </Text>

                    {props.items.map((shortcut) => (
                        <Tooltip
                            key={shortcut.id}
                            {...shortcut.tooltip}
                            placement="top"
                            openDelay={300}
                            px={2}
                            py={1}
                            bg="gray.50"
                            color="gray.900"
                            borderRadius="md"
                            closeOnClick={true}
                            shouldWrapChildren={true}
                        >
                            <Button
                                {...shortcut.button}
                                size="sm"
                                variant="outline"
                                borderColor="whiteAlpha.300"
                                color="whiteAlpha.800"
                                bg="none"
                                _hover={{
                                    bg: 'whiteAlpha.100',
                                    borderColor: 'whiteAlpha.400',
                                }}
                                leftIcon={
                                    shortcut.icon.kind === 'emoji' ? (
                                        <Text fontSize="md">{shortcut.icon.value}</Text>
                                    ) : (
                                        <Icon as={shortcut.icon.Icon} fontSize="md" />
                                    )
                                }
                            >
                                {shortcut.label}
                            </Button>
                        </Tooltip>
                    ))}
                    {props.truncated && (
                        <Menu>
                            <MenuButton
                                as={Button}
                                size="sm"
                                variant="outline"
                                borderColor="whiteAlpha.300"
                                color="whiteAlpha.800"
                                bg="none"
                                _hover={{
                                    bg: 'whiteAlpha.100',
                                    borderColor: 'whiteAlpha.400',
                                }}
                                rightIcon={<ChevronDownIcon />}
                            >
                                More
                            </MenuButton>
                            <MenuList bg="gray.800" borderColor="whiteAlpha.300">
                                {props.truncated.items.map((shortcut) => (
                                    <Tooltip
                                        key={shortcut.id}
                                        {...shortcut.tooltip}
                                        placement="right"
                                        openDelay={300}
                                        px={2}
                                        py={1}
                                        bg="gray.50"
                                        color="gray.900"
                                        borderRadius="md"
                                        closeOnClick={false}
                                        shouldWrapChildren={true}
                                    >
                                        <MenuItem
                                            {...shortcut.button}
                                            bg="transparent"
                                            color="whiteAlpha.800"
                                            _hover={{
                                                bg: 'whiteAlpha.100',
                                            }}
                                            icon={
                                                shortcut.icon.kind === 'emoji' ? (
                                                    <Text fontSize="md">
                                                        {shortcut.icon.value}
                                                    </Text>
                                                ) : (
                                                    <Icon
                                                        as={shortcut.icon.Icon}
                                                        fontSize="md"
                                                    />
                                                )
                                            }
                                        >
                                            <VStack align="start" spacing={0}>
                                                <Text fontSize="sm">
                                                    {shortcut.label}
                                                </Text>
                                                {shortcut.description && (
                                                    <Text
                                                        fontSize="xs"
                                                        color="whiteAlpha.600"
                                                    >
                                                        {shortcut.description}
                                                    </Text>
                                                )}
                                            </VStack>
                                        </MenuItem>
                                    </Tooltip>
                                ))}
                            </MenuList>
                        </Menu>
                    )}
                </HStack>
            </VStack>
        );
    };

    const EditorComponents: AppEditorTheme = {
        Mark(props) {
            if (props.type === 'apply') {
                return (
                    <Box as="span" bg={alpha('green.300', 0.2)} color="inherit">
                        {props.children}
                    </Box>
                );
            }
            if (props.type === 'reject') {
                return (
                    <Box
                        as="span"
                        bg={alpha('red.300', 0.2)}
                        color="inherit"
                        textDecoration="line-through"
                    >
                        {props.children}
                    </Box>
                );
            }
            return <span>{props.children}</span>;
        },
        Toolbar(props) {
            return <ToolbarItemContainer as={SelectionToolbar} />;
        },
        Divider(props) {
            return <Divider borderWidth={1} borderColor="whiteAlpha.200" />;
        },
        Text(props) {
            return (
                <TextBlockContainer as={TextBlock} block={props.id}>
                    {props.children}
                </TextBlockContainer>
            );
        },
        Heading(props) {
            return (
                <HeadingBlockContainer
                    as={HeadingBlock}
                    block={props.id}
                    level={props.level}
                >
                    {props.children}
                </HeadingBlockContainer>
            );
        },
        BulletList(props) {
            return (
                <VStack align="start" spacing={0}>
                    {props.children}
                </VStack>
            );
        },
        ListItem(props) {
            return (
                <ListItemBlockContainer as={ListItemBlock} block={props.id}>
                    {props.children}
                </ListItemBlockContainer>
            );
        },
        Callout(props) {
            return (
                <CalloutBlockContainer
                    as={CalloutBlock}
                    block={props.id}
                    icon={props.icon}
                >
                    {props.children}
                </CalloutBlockContainer>
            );
        },
        Mention(props) {
            return <MentionSpanContainer as={Mention} item={props.mention} />;
        },
        Custom(props) {
            if (props.type === 'database_view') {
                return <DatabaseViewBlockContainer as={DatabaseBlock} block={props.id} />;
            }
            if (props.type === 'task') {
                return <TaskBlockContainer as={Task} block={props.id} />;
            }
            if (props.type === 'button') {
                return <ButtonBlockContainer as={BlockItemButton} block={props.id} />;
            }
            if (props.type === 'resource') {
                return <ResourceBlockContainer as={ResourceBlock} block={props.id} />;
            }
            if (props.type === 'form') {
                return <FormBlockContainer as={FormBlock} block={props.id} />;
            }
            return assertNever(props.type);
        },
        SuggestionPopover(props) {
            if (!props.isVisible) return null;

            return (
                <Popover
                    isOpen={props.isVisible}
                    placement="top"
                    closeOnBlur={false}
                    isLazy
                    lazyBehavior="unmount"
                >
                    <PopoverTrigger>
                        <Box position="absolute" width="1px" height="1px" />
                    </PopoverTrigger>
                    <PopoverContent
                        bg="black"
                        overflow="hidden"
                        w="auto"
                        border="none"
                        // bg="none"
                        _focus={{ outline: 'none', boxShadow: 'none' }}
                    >
                        <PopoverBody
                            as={HStack}
                            border="none"
                            bg="whiteAlpha.200"
                            p={1}
                            spacing={1}
                        >
                            <Tooltip
                                label="Accept change"
                                placement="top"
                                openDelay={300}
                                px={2}
                                py={1}
                                bg="gray.50"
                                color="gray.900"
                                borderRadius="md"
                                closeOnClick={false}
                                shouldWrapChildren={true}
                            >
                                <IconButton
                                    aria-label="Accept"
                                    icon={<Icon fontSize="sm" as={FiCheck} />}
                                    h={5}
                                    w={5}
                                    minW={5}
                                    borderRadius="sm"
                                    colorScheme="green"
                                    onClick={props.onAccept}
                                    // borderLeftRadius="md"
                                    // borderRightRadius="none"
                                />
                            </Tooltip>
                            <Tooltip
                                label="Reject change"
                                placement="top"
                                openDelay={300}
                                px={2}
                                py={1}
                                bg="gray.50"
                                color="gray.900"
                                borderRadius="md"
                                closeOnClick={false}
                                shouldWrapChildren={true}
                            >
                                <IconButton
                                    aria-label="Reject"
                                    icon={<Icon fontSize="sm" as={FiX} />}
                                    h={5}
                                    w={5}
                                    minW={5}
                                    borderRadius="sm"
                                    colorScheme="red"
                                    onClick={props.onReject}
                                    // borderRightRadius="md"
                                    // borderLeftRadius="none"
                                />
                            </Tooltip>
                        </PopoverBody>
                    </PopoverContent>
                </Popover>
            );
        },
    };

    return (props) => {
        const contentRef = useRef<HTMLDivElement>(null);
        const theme = useTheme();

        return (
            <Grid
                h="full"
                w="full"
                gap={{ base: 4, md: 6, lg: 8, xl: 12 }}
                templateRows="min-content 1fr"
            >
                {/* Header */}

                <GridItem p={{ base: 2, md: 3, lg: 4, xl: 6 }}>
                    <HStack w="full" justify="space-between" align="start">
                        <Breadcrumb
                            userSelect="none"
                            separator={
                                <Icon as={HiOutlineSlash} color="whiteAlpha.400" />
                            }
                            spacing={0}
                        >
                            <BreadcrumbItem>
                                <BreadcrumbLink
                                    as={Button}
                                    color="whiteAlpha.600"
                                    bg="none"
                                    size="sm"
                                    minH={0}
                                    textDecor="none"
                                    fontWeight="medium"
                                    px={2}
                                    py={0.5}
                                    _hover={{
                                        bg: 'whiteAlpha.50',
                                    }}
                                >
                                    Workspace
                                </BreadcrumbLink>
                            </BreadcrumbItem>
                            <BreadcrumbItem>
                                <HStack
                                    maxW="20rem"
                                    bg="none"
                                    fontSize="sm"
                                    minH={0}
                                    textDecor="none"
                                    fontWeight="medium"
                                    userSelect="none"
                                    cursor="pointer"
                                    px={2}
                                    py={0.5}
                                    borderRadius="md"
                                    spacing={2}
                                    _hover={{
                                        bg: 'whiteAlpha.50',
                                    }}
                                >
                                    {props.item.icon.kind === 'emoji' && (
                                        <Text fontSize="md">{props.item.icon.text}</Text>
                                    )}
                                    {props.item.icon.kind === 'icon' && (
                                        <Icon
                                            fontSize="md"
                                            color="whiteAlpha.600"
                                            as={props.item.icon.Component}
                                        />
                                    )}
                                    <Text
                                        noOfLines={1}
                                        overflow="hidden"
                                        w="full"
                                        textOverflow="ellipsis"
                                        color="whiteAlpha.600"
                                    >
                                        {props.item.title}
                                    </Text>
                                </HStack>
                            </BreadcrumbItem>
                        </Breadcrumb>

                        <HStack spacing={3}>
                            <AvatarGroup userSelect="none" size="sm" max={2} spacing={-3}>
                                <Avatar name="Agent Susie" bg="blue.300" />
                                <Avatar name="You" bg="green.300" />
                                <Avatar name="Agent Jane" bg="red.300" />
                            </AvatarGroup>
                            {/* <Tag py={2} px={3} colorScheme="blue" borderRadius="lg">
                                <HStack spacing={2}>
                                    <Icon fontSize="lg" as={AiOutlineEdit} />
                                    <Text>Suggesting edits</Text>
                                    <Icon fontSize="sm" as={AiOutlineClose} />
                                </HStack>
                            </Tag> */}
                            <HStack spacing={1}>
                                {props.actions.map((action) => (
                                    <Tooltip
                                        {...action.tooltip}
                                        key={action.id}
                                        placement="bottom"
                                        openDelay={300}
                                        px={2}
                                        py={1}
                                        bg="gray.50"
                                        color="gray.900"
                                        borderRadius="md"
                                        closeOnClick={false}
                                        shouldWrapChildren={true}
                                    >
                                        <IconButton
                                            {...action.button}
                                            onFocus={(event) => event.preventDefault()}
                                            aria-label={action.id}
                                            size="sm"
                                            bg="none"
                                            _hover={{
                                                bg: 'whiteAlpha.50',
                                            }}
                                            icon={
                                                <Icon
                                                    fontSize="lg"
                                                    color="whiteAlpha.800"
                                                    as={action.Icon}
                                                />
                                            }
                                        />
                                    </Tooltip>
                                ))}
                            </HStack>
                            <ButtonGroup
                                isAttached={true}
                                borderWidth={1}
                                borderColor="whiteAlpha.300"
                                variant="outline"
                                borderRadius="full"
                                overflow="hidden"
                            >
                                <Button
                                    color="whiteAlpha.800"
                                    bg="none"
                                    size="sm"
                                    _hover={{
                                        bg: 'whiteAlpha.50',
                                    }}
                                    borderRightWidth={1}
                                    borderRightColor="whiteAlpha.300"
                                >
                                    Share
                                </Button>
                                <IconButton
                                    aria-label="more"
                                    bg="none"
                                    size="sm"
                                    _hover={{
                                        bg: 'whiteAlpha.50',
                                    }}
                                    icon={<Icon fontSize="lg" as={ChevronDownIcon} />}
                                />
                            </ButtonGroup>
                        </HStack>
                    </HStack>
                </GridItem>

                {/* Content */}

                <Grid
                    as={GridItem}
                    maxW="48rem"
                    w="full"
                    templateRows={'min-content 1fr'}
                    mx="auto"
                    gap={0}
                    position="relative"
                >
                    {/* Header Section */}
                    <GridItem pl={styles.offset}>
                        <VStack align="start" spacing={8}>
                            {props.item.icon.kind === 'emoji' && (
                                <Box
                                    fontSize="7xl"
                                    width="1em"
                                    height="1em"
                                    display="flex"
                                    alignItems="center"
                                    justifyContent="center"
                                    borderRadius="md"
                                    cursor="pointer"
                                    _hover={{ bg: 'whiteAlpha.50' }}
                                >
                                    <Text lineHeight={1}>{props.item.icon.text}</Text>
                                </Box>
                            )}
                            <HStack spacing={4} align="start">
                                <Heading
                                    as="h1"
                                    fontWeight="semibold"
                                    fontSize="3xl"
                                    color="whiteAlpha.900"
                                >
                                    {props.item.title}
                                </Heading>
                            </HStack>
                        </VStack>
                    </GridItem>

                    {/* Editor Section */}
                    <GridItem
                        ref={contentRef}
                        pr={10} // offset left-hand block hover menu
                        position="relative"
                        minH={0}
                        pb={{ base: 32, xl: 64 }}
                    >
                        <Editor as={EditorComponents} {...props.editor} />

                        {/* Floating Suggestion Action Menu */}
                        {props.suggestion && (
                            <Box
                                position="fixed"
                                bottom={{ base: 8, xl: 16, '2xl': 24 }}
                                left="50%"
                                transform="translateX(-50%)"
                                zIndex={1000}
                                borderWidth={1}
                                borderColor="whiteAlpha.300"
                                borderRadius="md"
                                bg="black"
                                shadow="xl"
                                overflow="hidden"
                            >
                                <HStack
                                    px={4}
                                    py={3}
                                    spacing={{ base: 12, '2xl': 24 }}
                                    align="center"
                                    bg="whiteAlpha.100"
                                >
                                    <HStack spacing={2} align="center">
                                        <Icon
                                            as={PiMagicWand}
                                            color="blue.300"
                                            fontSize="lg"
                                        />
                                        <Text
                                            color="whiteAlpha.800"
                                            fontWeight="medium"
                                            fontSize="sm"
                                        >
                                            Pending suggestions
                                        </Text>
                                    </HStack>
                                    <HStack spacing={2}>
                                        <Button
                                            {...props.suggestion.reject.button}
                                            size="sm"
                                            h={7}
                                            px={2}
                                            bg="none"
                                            borderRadius="sm"
                                            color="whiteAlpha.600"
                                            _hover={{
                                                bg: 'whiteAlpha.100',
                                            }}
                                        >
                                            {props.suggestion.reject.label}
                                        </Button>
                                        <Button
                                            {...props.suggestion.accept.button}
                                            size="sm"
                                            h={7}
                                            px={2}
                                            bg="blue.400"
                                            borderRadius="sm"
                                            _hover={{ bg: 'blue.500' }}
                                        >
                                            {props.suggestion.accept.label}
                                        </Button>
                                    </HStack>
                                </HStack>
                            </Box>
                        )}
                    </GridItem>
                </Grid>
                {/* Shortcuts Section */}

                {props.shortcut && (
                    <Box
                        maxW="48rem"
                        mx="auto"
                        position="absolute"
                        bottom={0}
                        left={0}
                        right={0}
                        pl={styles.offset}
                        pb={{ base: 12, xl: 16, '2xl': 24 }}
                    >
                        <ShortcutContainer
                            as={ShortcutView}
                            contentId={props.shortcut.contentId}
                        />
                    </Box>
                )}
            </Grid>
        );
    };
}
